import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from 'react';
import gsap from 'gsap';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';
import { SplitText } from 'gsap/SplitText';
import { CustomEase } from 'gsap/CustomEase';
import { Physics2DPlugin } from 'gsap/Physics2DPlugin';
import { t } from '@/i18n';

gsap.registerPlugin(DrawSVGPlugin, ScrambleTextPlugin, SplitText, CustomEase, Physics2DPlugin);

/**
 * "BEARING 047 — Lock-On at Periscope Depth"
 *
 * The login page is a live submarine sonar scope: a rotating beam with
 * phosphor persistence, invoice-numbered contacts, marine snow and a bearing
 * ring, all in the brand's deep indigo. The login card is the one contact the
 * scope was hunting — the entrance choreography sweeps, brakes at bearing
 * 047 and locks four brackets onto the card ("KONTAKT ZIDENTYFIKOWANY").
 *
 * Render stack (bottom → top):
 *   1. CSS depth gradient (Abyssal Ink → Pressure Indigo)
 *   2. <canvas> fresh layer — marine snow, caustic shaft, ping rings
 *      (cleared every frame)
 *   3. <canvas> phosphor layer — sweep beam + contact blips, faded each
 *      frame via destination-out so the trail/afterglow comes for free
 *   4. SVG overlay — bearing ring, 72 ticks, range rings, crosshair
 *      hairlines (DrawSVG-crisp linework)
 *   5. DOM — instrument readouts, the card (children) with lock-on
 *      brackets and the brass classification tag
 *
 * The page deliberately opts OUT of theme switching (forced `dark` class):
 * a sonar scope has no light mode.
 */

export interface SonarScopeHandle {
  /** One strong ping emitted from the card + bubble burst (on submit). */
  submitPing: () => void;
  /** Calm double-echo + the tag briefly rescrambles to "POWTÓRZ NAMIAR". */
  errorEcho: () => void;
  /** "Surfacing" exit on auth success; onDone fires when the lift lands. */
  surface: (onDone: () => void) => void;
}

// ---- palette (concept tokens; brass is hard-capped to tag + lens glint) ----
const INK = '#0b0a1f';
const PRESSURE = '#1e1b4b';
const BEAM_RGB = '79,70,229'; // #4f46e5
const PHOSPHOR_RGB = '165,180,252'; // #a5b4fc
const BRASS = '#e8b45a';

const DEG = Math.PI / 180;
const SEEN_KEY = 'periscope-scope-seen';
const AMBIENT_REV_S = 14;
const LOCK_BEARING = 47;

interface Contact {
  bearing: number; // degrees, 0 = north/up
  dist: number; // 0..1 of beam radius
  label: string;
  lastHit: number; // elapsed seconds when the beam last swept it (-Infinity = never)
}

interface Ping {
  born: number; // seconds (elapsed clock)
  x: number;
  y: number;
  maxR: number;
  strength: number; // peak alpha
}

interface Particle {
  x: number;
  y: number;
  z: number; // depth layer factor (parallax + speed)
  r: number;
  sway: number;
  phase: number;
}

function randomInvoiceLabel(): string {
  const n = 100 + Math.floor(Math.random() * 900);
  return `FV 2026/07/${n}`;
}

function makeContact(): Contact {
  return {
    bearing: Math.random() * 360,
    dist: 0.3 + Math.random() * 0.62,
    label: randomInvoiceLabel(),
    lastHit: -Infinity,
  };
}

/** Pre-rendered radial glow sprite — cheaper than per-frame shadowBlur. */
function makeGlowSprite(size: number, rgb: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const m = size / 2;
  const grad = g.createRadialGradient(m, m, 0, m, m, m);
  grad.addColorStop(0, `rgba(${rgb},0.9)`);
  grad.addColorStop(0.3, `rgba(${rgb},0.35)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export const SonarScope = forwardRef<SonarScopeHandle, { children: ReactNode }>(
  function SonarScope({ children }, ref) {
    const rootRef = useRef<HTMLDivElement>(null);
    const blackoutRef = useRef<HTMLDivElement>(null);
    const surfaceRef = useRef<HTMLDivElement>(null);
    const freshRef = useRef<HTMLCanvasElement>(null);
    const phosphorRef = useRef<HTMLCanvasElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const readoutLeftRef = useRef<HTMLDivElement>(null);
    const depthRef = useRef<HTMLSpanElement>(null);
    const bearingRef = useRef<HTMLDivElement>(null);
    const cardWrapRef = useRef<HTMLDivElement>(null);
    const tagRef = useRef<HTMLDivElement>(null);

    const apiRef = useRef<SonarScopeHandle>({
      submitPing: () => {},
      errorEcho: () => {},
      surface: (onDone) => onDone(),
    });
    useImperativeHandle(ref, () => ({
      submitPing: () => apiRef.current.submitPing(),
      errorEcho: () => apiRef.current.errorEcho(),
      surface: (onDone) => apiRef.current.surface(onDone),
    }));

    useEffect(() => {
      const root = rootRef.current;
      const fresh = freshRef.current;
      const phosphor = phosphorRef.current;
      const svg = svgRef.current;
      const cardWrap = cardWrapRef.current;
      if (!root || !fresh || !phosphor || !svg || !cardWrap) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const seen = sessionStorage.getItem(SEEN_KEY) === '1';

      const fctx = fresh.getContext('2d')!;
      const pctx = phosphor.getContext('2d')!;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

      // ---- scene state ----------------------------------------------------
      let w = 0;
      let h = 0;
      let cx = 0;
      let cy = 0;
      let beamR = 0;
      const beam = { deg: 0, alpha: 0 };
      let prevBeamDeg = 0;
      let elapsed = 0;
      const contacts: Contact[] = Array.from({ length: 8 }, makeContact);
      const pings: Ping[] = [];
      let particles: Particle[] = [];
      let particleTarget = 130;
      const pointer = { x: 0, y: 0 }; // parallax offset, lerped
      const pointerTarget = { x: 0, y: 0 };
      let activity = 1; // "quiet sea" factor — decays after 90 s of no input
      let lastInput = performance.now();
      let causticOn = true;
      // rolling FPS → adaptive particle budget
      let fpsFrames = 0;
      let fpsLast = performance.now();
      let fpsLowStreak = 0;

      const glowContact = makeGlowSprite(Math.round(46 * dpr), PHOSPHOR_RGB);
      const glowCore = makeGlowSprite(Math.round(14 * dpr), '224,231,255');

      const seedParticles = () => {
        particles = Array.from({ length: particleTarget }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          z: 0.4 + Math.random() * 1.1,
          r: 0.6 + Math.random() * 1.3,
          sway: 6 + Math.random() * 14,
          phase: Math.random() * Math.PI * 2,
        }));
      };

      // ---- SVG overlay (bearing ring, ticks, range rings, hairlines) ------
      // Built imperatively: geometry depends on viewport pixels and is
      // re-laid-out on resize; GSAP animates the same nodes.
      const svgKids: SVGElement[] = [];
      const mkSvg = <K extends keyof SVGElementTagNameMap>(
        tag: K,
        attrs: Record<string, string | number>,
      ): SVGElementTagNameMap[K] => {
        const el = document.createElementNS(SVG_NS, tag);
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
        svg.appendChild(el);
        svgKids.push(el);
        return el;
      };

      let ringMain: SVGCircleElement | null = null;
      let ringInner: SVGCircleElement | null = null;
      let ringOuter: SVGCircleElement | null = null;
      let ticks: SVGLineElement[] = [];
      let hairlines: SVGLineElement[] = [];

      const buildSvg = () => {
        for (const el of svgKids) el.remove();
        svgKids.length = 0;
        ticks = [];
        hairlines = [];

        const rMain = Math.min(w, h) * 0.44;
        const stroke = `rgba(${PHOSPHOR_RGB},0.28)`;
        ringInner = mkSvg('circle', {
          cx, cy, r: rMain * 0.5, fill: 'none',
          stroke: `rgba(${PHOSPHOR_RGB},0.10)`, 'stroke-width': 1,
        });
        ringOuter = mkSvg('circle', {
          cx, cy, r: rMain * 1.65, fill: 'none',
          stroke: `rgba(${PHOSPHOR_RGB},0.07)`, 'stroke-width': 1,
        });
        ringMain = mkSvg('circle', {
          cx, cy, r: rMain, fill: 'none', stroke, 'stroke-width': 1,
        });
        for (let i = 0; i < 72; i++) {
          const a = i * 5 * DEG - Math.PI / 2;
          const long = i % 6 === 0;
          const r1 = rMain - (long ? 14 : 7);
          ticks.push(
            mkSvg('line', {
              x1: cx + Math.cos(a) * r1,
              y1: cy + Math.sin(a) * r1,
              x2: cx + Math.cos(a) * rMain,
              y2: cy + Math.sin(a) * rMain,
              stroke: `rgba(${PHOSPHOR_RGB},${long ? 0.4 : 0.22})`,
              'stroke-width': 1,
            }),
          );
        }
        // Crosshair hairlines: drawn inward from the viewport edges,
        // terminating exactly at the card's edges — the optics physically
        // plug into the contact. Coordinates are ROOT-relative (the SVG
        // fills the root), so a scrolled or over-tall page can't skew them.
        const rootRect = root.getBoundingClientRect();
        const cardRect = cardWrap.getBoundingClientRect();
        const rect = {
          left: cardRect.left - rootRect.left,
          right: cardRect.right - rootRect.left,
          top: cardRect.top - rootRect.top,
          bottom: cardRect.bottom - rootRect.top,
        };
        const gap = 18;
        const hl = `rgba(${PHOSPHOR_RGB},0.3)`;
        hairlines.push(
          mkSvg('line', { x1: 0, y1: cy, x2: rect.left - gap, y2: cy, stroke: hl, 'stroke-width': 1 }),
          mkSvg('line', { x1: w, y1: cy, x2: rect.right + gap, y2: cy, stroke: hl, 'stroke-width': 1 }),
          mkSvg('line', { x1: cx, y1: 0, x2: cx, y2: rect.top - gap, stroke: hl, 'stroke-width': 1 }),
          mkSvg('line', { x1: cx, y1: h, x2: cx, y2: rect.bottom + gap, stroke: hl, 'stroke-width': 1 }),
        );
      };

      const layout = () => {
        w = root.clientWidth;
        h = root.clientHeight;
        cx = w / 2;
        cy = h / 2;
        beamR = Math.max(w, h) * 0.78;
        for (const c of [fresh, phosphor]) {
          c.width = Math.round(w * dpr);
          c.height = Math.round(h * dpr);
        }
        fctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        pctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      };

      layout();
      seedParticles();

      // ---- canvas painters -------------------------------------------------
      const contactXY = (c: Contact): [number, number] => {
        const a = (c.bearing - 90) * DEG;
        return [cx + Math.cos(a) * c.dist * beamR, cy + Math.sin(a) * c.dist * beamR];
      };

      /**
       * The beam is stamped as the SECTOR SWEPT since the previous frame
       * (plus a small pad), so any rotation speed produces a continuous
       * wedge — no ray-gap banding during the fast entrance revolution.
       */
      const paintBeam = () => {
        if (beam.alpha <= 0.01) return;
        // wrap the per-frame delta so the ambient tween's repeat restart
        // (an instant -360° jump) doesn't stamp a fat wedge every 14 s
        let sweptRaw = Math.abs(beam.deg - prevBeamDeg) % 360;
        if (sweptRaw > 180) sweptRaw = 360 - sweptRaw;
        const swept = Math.min(sweptRaw, 30);
        // dwell dimming: repeated stamps at one bearing (the lock-on brake)
        // must not saturate to white — equilibrium vs the fade rate instead
        const dwell = Math.min(1, 0.15 + swept / 0.5);
        const a = (beam.deg - 90) * DEG;
        pctx.save();
        pctx.translate(cx, cy);
        pctx.rotate(a);
        pctx.globalCompositeOperation = 'lighter';
        // radially-symmetric falloff so abutting per-frame sectors join
        // without gradient-axis seams
        const grad = pctx.createRadialGradient(0, 0, 0, 0, 0, beamR);
        grad.addColorStop(0, `rgba(${BEAM_RGB},${0.16 * beam.alpha * dwell})`);
        grad.addColorStop(0.45, `rgba(${BEAM_RGB},${0.09 * beam.alpha * dwell})`);
        grad.addColorStop(1, `rgba(${BEAM_RGB},0)`);
        pctx.fillStyle = grad;
        pctx.beginPath();
        pctx.moveTo(0, 0);
        // exactly the sector swept since last frame (tiny anti-alias pad
        // only) — continuous at any rotation speed with no overlap seams
        pctx.arc(0, 0, beamR, -swept * DEG - 0.002, 0.002);
        pctx.closePath();
        pctx.fill();
        // crisp leading edge: faded at high speed (no spoke fans on the
        // entrance rev) AND at standstill (repeated stamps at one angle
        // would saturate white while parked at bearing 047)
        const edge =
          0.4 *
          beam.alpha *
          Math.max(0, 1 - swept / 2.5) *
          Math.max(0.06, Math.min(1, swept / 0.4));
        if (edge > 0.02) {
          pctx.strokeStyle = `rgba(${PHOSPHOR_RGB},${edge})`;
          pctx.lineWidth = 1;
          pctx.beginPath();
          pctx.moveTo(0, 0);
          pctx.lineTo(beamR, 0);
          pctx.stroke();
        }
        pctx.restore();
      };

      /** Mark contacts the beam swept across since last frame as hit. */
      const registerSweptContacts = () => {
        const from = prevBeamDeg;
        const to = beam.deg;
        if (beam.alpha <= 0.05 || to === from) return;
        const span = ((to - from) % 360 + 360) % 360;
        if (span <= 0 || span >= 180) return;
        for (const c of contacts) {
          const rel = ((c.bearing - from) % 360 + 360) % 360;
          if (rel <= span) c.lastHit = elapsed;
        }
      };

      /**
       * Contacts live on the FRESH layer with their own ~6 s phosphor
       * persistence (crisp labels at any decay stage, independent of the
       * beam trail's much faster fade).
       */
      const paintContacts = () => {
        for (const c of contacts) {
          const age = elapsed - c.lastHit;
          if (age > 7) continue;
          const decay = Math.exp(-age / 2.6);
          const [x, y] = contactXY(c);
          fctx.globalAlpha = Math.min(1, decay) * 0.95;
          const gs = glowContact.width / dpr;
          fctx.drawImage(glowContact, x - gs / 2, y - gs / 2, gs, gs);
          const cs = glowCore.width / dpr;
          fctx.drawImage(glowCore, x - cs / 2, y - cs / 2, cs, cs);
          fctx.font = `10px 'JetBrains Mono', ui-monospace, monospace`;
          fctx.fillStyle = `rgba(${PHOSPHOR_RGB},0.85)`;
          fctx.fillText(c.label, x + 10, y - 8);
          fctx.globalAlpha = 1;
        }
      };

      const paintFresh = (dt: number) => {
        fctx.clearRect(0, 0, w, h);
        // caustic light shaft breathing from the surface above; the gradient
        // radius reaches past the farthest viewport corner so its edge never
        // shows as an arc inside the frame
        if (causticOn) {
          const breathe = 0.5 + 0.5 * Math.sin((elapsed / 20) * Math.PI * 2);
          const gy = -h * 0.35;
          const reach = Math.hypot(Math.max(cx, w - cx), h - gy) * 1.05;
          const g = fctx.createRadialGradient(cx, gy, 0, cx, gy, reach);
          g.addColorStop(0, `rgba(${PHOSPHOR_RGB},${0.05 + 0.035 * breathe})`);
          g.addColorStop(0.5, `rgba(${PHOSPHOR_RGB},${(0.05 + 0.035 * breathe) * 0.28})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          fctx.fillStyle = g;
          fctx.fillRect(0, 0, w, h);
        }
        // marine snow — three parallax depth layers, drifting up with sway
        fctx.fillStyle = `rgba(${PHOSPHOR_RGB},0.35)`;
        for (const p of particles) {
          p.y -= (4 + p.z * 7) * dt * activity;
          if (p.y < -4) {
            p.y = h + 4;
            p.x = Math.random() * w;
          }
          const px = p.x + Math.sin(elapsed * 0.4 + p.phase) * p.sway + pointer.x * p.z * 7;
          const py = p.y + pointer.y * p.z * 5;
          fctx.globalAlpha = 0.1 + p.z * 0.16;
          fctx.fillRect(px, py, p.r, p.r);
        }
        fctx.globalAlpha = 1;
        paintContacts();
        // ping rings — violent attack, long exponential decay
        for (let i = pings.length - 1; i >= 0; i--) {
          const ping = pings[i];
          const u = (elapsed - ping.born) / 3;
          if (u >= 1) {
            pings.splice(i, 1);
            continue;
          }
          const r = ping.maxR * (1 - Math.pow(1 - u, 2.6));
          const alpha = ping.strength * Math.pow(1 - u, 1.9);
          fctx.strokeStyle = `rgba(${PHOSPHOR_RGB},${alpha})`;
          fctx.lineWidth = 1.5;
          fctx.beginPath();
          fctx.arc(ping.x, ping.y, r, 0, Math.PI * 2);
          fctx.stroke();
        }
      };

      const paintPhosphor = () => {
        pctx.globalCompositeOperation = 'destination-out';
        pctx.fillStyle = 'rgba(0,0,0,0.045)';
        pctx.fillRect(0, 0, w, h);
        registerSweptContacts();
        paintBeam();
        pctx.globalCompositeOperation = 'source-over';
      };

      const addPing = (x: number, y: number, strength: number, maxR = beamR * 0.7) => {
        pings.push({ born: elapsed, x, y, maxR, strength });
      };

      // ---- the single rAF authority ---------------------------------------
      let frame = 0;
      let pendingDt = 0;
      const tick = (_time: number, deltaMS: number) => {
        const dt = Math.min(deltaMS / 1000, 0.1);
        elapsed += dt;
        pendingDt += dt;
        frame++;

        // quiet sea: an open but untouched tab decays to near-zero motion
        // AND near-zero work — while idle only every 3rd frame repaints
        const idleFor = performance.now() - lastInput;
        const idle = idleFor > 90_000;
        const targetActivity = idle ? 0.3 : 1;
        activity += (targetActivity - activity) * 0.02;
        if (idle && frame % 3 !== 0) return;
        pointer.x += (pointerTarget.x - pointer.x) * 0.06;
        pointer.y += (pointerTarget.y - pointer.y) * 0.06;

        // north-crossing ambient ping — only a true forward wrap (large
        // drop) counts; the lock-on brake's overshoot backswing moves the
        // bearing backwards by fractions of a degree and must not ping
        if (beam.alpha > 0.5) {
          const wrapped = ((beam.deg % 360) + 360) % 360;
          const prevWrapped = ((prevBeamDeg % 360) + 360) % 360;
          if (wrapped < prevWrapped && prevWrapped - wrapped > 180) {
            addPing(cx, cy, 0.22 * activity);
          }
        }

        paintFresh(pendingDt);
        pendingDt = 0;
        paintPhosphor();
        prevBeamDeg = beam.deg;

        // derived readout — computed from the same value driving the sweep,
        // so instrument and label can never desync
        if (frame % 6 === 0 && bearingRef.current && beam.alpha > 0.1) {
          const b = (((beam.deg % 360) + 360) % 360).toFixed(1).padStart(5, '0');
          bearingRef.current.textContent = t('auth.scopeBearing', { deg: b });
        }

        // adaptive particle budget: rolling FPS < 45 for 2 s → shrink
        // (suspended while idle — the deliberate frame-skip is not jank)
        fpsFrames++;
        const now = performance.now();
        if (now - fpsLast >= 1000) {
          const fps = fpsFrames;
          fpsFrames = 0;
          fpsLast = now;
          fpsLowStreak = fps < 45 && !idle ? fpsLowStreak + 1 : 0;
          if (fpsLowStreak >= 2) {
            fpsLowStreak = 0;
            particleTarget = Math.max(40, Math.floor(particleTarget * 0.6));
            particles.length = Math.min(particles.length, particleTarget);
            if (particleTarget <= 60) causticOn = false;
          }
        }
      };

      // ---- input listeners --------------------------------------------------
      const onPointerMove = (e: PointerEvent) => {
        lastInput = performance.now();
        if (coarsePointer || reduced) return;
        pointerTarget.x = (e.clientX / w) * 2 - 1;
        pointerTarget.y = (e.clientY / h) * 2 - 1;
      };
      const onAnyInput = () => {
        lastInput = performance.now();
      };
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('keydown', onAnyInput, { passive: true });
      window.addEventListener('pointerdown', onAnyInput, { passive: true });

      const onResize = () => {
        layout();
        buildSvg();
        // a resize mid-entrance orphans tweens targeting the rebuilt SVG
        // nodes — jump the choreography to its end first
        if (masterRef && masterRef.progress() < 1) {
          skipped = true;
          masterRef.progress(1, false);
        }
        // re-apply RESTING state to the rebuilt linework (hairlines rest
        // dim, not at full draw-time brightness)
        gsap.set([ringMain, ringInner, ringOuter, ...ticks], { opacity: 1 });
        gsap.set(hairlines, { opacity: reduced ? 1 : 0.16, drawSVG: '100%' });
        if (reduced) paintStaticScene();
      };
      window.addEventListener('resize', onResize);

      // assigned inside the gsap.context callback (which runs synchronously),
      // so they must be declared before it
      let skipCleanup: (() => void) | null = null;
      let paintStaticScene: () => void = () => {};
      let skipped = false;
      let masterRef: gsap.core.Timeline | null = null;
      // Ambient animations are created from the master timeline's onComplete
      // (i.e. AFTER the gsap.context callback returned), so the context never
      // records them — track them explicitly and kill them in cleanup, or
      // they would tick for the rest of the SPA session after login.
      const ambient: { kill: () => void }[] = [];

      // ---- GSAP choreography ------------------------------------------------
      const gctx = gsap.context(() => {
        CustomEase.create(
          'sweepBrake',
          // fast attack, mechanical deceleration, tiny handwheel overshoot
          'M0,0 C0.09,0.35 0.2,0.74 0.42,0.9 0.62,1.028 0.78,1.005 1,1',
        );

        const tagEl = tagRef.current!;
        const brackets = cardWrap.querySelectorAll<HTMLElement>('[data-scope-bracket]');
        const logoStrokes = root.querySelectorAll<SVGPathElement>('[data-scope-logo] path');
        const logoLens = root.querySelector<SVGCircleElement>('[data-scope-logo] circle');
        const tagline = root.querySelector<HTMLElement>('[data-scope-tagline]');
        const TAG_TEXT = t('auth.scopeContact');
        const TAG_RETRY = t('auth.scopeRetry');
        const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUWXYZĄĆĘŁŃÓŚŹŻ0123456789·';

        const readouts = [readoutLeftRef.current!, bearingRef.current!];

        function paintStaticSceneInner() {
          // a fully composed still: the instrument paused, not switched off,
          // contacts frozen at staggered decay as if the sweep just passed
          pctx.clearRect(0, 0, w, h);
          beam.deg = LOCK_BEARING;
          prevBeamDeg = LOCK_BEARING - 1;
          beam.alpha = 1;
          paintBeam();
          pctx.globalCompositeOperation = 'source-over';
          fctx.clearRect(0, 0, w, h);
          fctx.fillStyle = `rgba(${PHOSPHOR_RGB},0.2)`;
          for (const p of particles) fctx.fillRect(p.x, p.y, p.r, p.r);
          contacts.forEach((c, i) => {
            c.lastHit = -(i % 4) * 1.6;
          });
          paintContacts();
        }
        paintStaticScene = paintStaticSceneInner;

        buildSvg();

        // initial states
        gsap.set(cardWrap, { opacity: 0, y: 14, scale: 0.965 });
        gsap.set(brackets, { opacity: 0 });
        gsap.set(readouts, { opacity: 0 });
        gsap.set(tagEl, { opacity: 0 });
        if (tagline) gsap.set(tagline, { opacity: 0 });

        if (reduced) {
          paintStaticSceneInner();
          gsap.set([ringMain, ringInner, ringOuter, ...ticks, ...hairlines], { opacity: 1 });
          gsap.set(brackets, { opacity: 0.4, x: 0, y: 0 });
          gsap.set(readouts, { opacity: 1 });
          gsap.set(tagEl, { opacity: 0.75 });
          tagEl.textContent = TAG_TEXT;
          if (tagline) gsap.set(tagline, { opacity: 1 });
          if (logoStrokes.length) gsap.set(logoStrokes, { drawSVG: '100%' });
          gsap.set(blackoutRef.current, { opacity: 0 });
          // opacity only — reduced motion means no transform movement at all
          gsap.set(cardWrap, { y: 0, scale: 1 });
          gsap.fromTo(cardWrap, { opacity: 0 }, { opacity: 1, duration: 0.15 });
          if (bearingRef.current) {
            bearingRef.current.textContent = t('auth.scopeBearing', { deg: '047.0' });
          }
          return; // no ticker, no loops — a paused instrument
        }

        gsap.ticker.add(tick);

        // ---- ambient loop (started when the entrance hands over) ----------
        let ambientStarted = false;
        const startAmbient = () => {
          if (ambientStarted) return;
          ambientStarted = true;
          // one revolution / 14 s forever; the repeat restart is invisible
          // because a 360° jump is identity on the scope
          ambient.push(gsap.to(beam, { deg: '+=360', duration: AMBIENT_REV_S, ease: 'none', repeat: -1 }));
          // every 40–70 s one contact retires and a new one appears
          const rotateContacts = () => {
            const idx = Math.floor(Math.random() * contacts.length);
            contacts[idx] = makeContact();
            ambient.push(gsap.delayedCall(40 + Math.random() * 30, rotateContacts));
          };
          ambient.push(gsap.delayedCall(40 + Math.random() * 30, rotateContacts));
          // depth read-out flutter — the boat is breathing
          const depthFlutter = () => {
            if (depthRef.current) {
              const m = 13 + Math.floor(Math.random() * 3);
              ambient.push(
                gsap.to(depthRef.current, {
                  duration: 0.5,
                  scrambleText: { text: t('auth.scopeDepth', { m }), chars: '0123456789' },
                }),
              );
            }
            ambient.push(gsap.delayedCall(20 + Math.random() * 25, depthFlutter));
          };
          ambient.push(gsap.delayedCall(12, depthFlutter));
        };

        // ---- entrance -------------------------------------------------------
        const master = gsap.timeline({
          defaults: { ease: 'power2.out' },
          onComplete: () => {
            sessionStorage.setItem(SEEN_KEY, '1');
            startAmbient();
          },
        });
        masterRef = master;

        if (!seen) {
          // FULL LOCK-ON (~3 s), card interactive throughout (opacity only)
          gsap.set([ringMain, ringInner, ringOuter], { drawSVG: '0%' });
          gsap.set(ticks, { opacity: 0 });
          gsap.set(hairlines, { drawSVG: '0%', opacity: 0.55 });

          master
            // Beat 1 — black lifts into the depth gradient
            .to(blackoutRef.current, { opacity: 0, duration: 0.6, ease: 'sine.out' }, 0)
            // Beat 2 — the instrument draws itself
            .to(ringMain, { drawSVG: '100%', duration: 0.9, ease: 'power2.inOut' }, 0.2)
            .to([ringInner, ringOuter], { drawSVG: '100%', duration: 0.7, ease: 'power2.inOut' }, 0.45)
            .to(ticks, { opacity: 1, duration: 0.05, stagger: { each: 0.012, from: 'random' } }, 0.4)
            .to(readouts, { opacity: 1, duration: 0.5 }, 0.7)
            // Beat 3 — first ping breathes out from behind the card
            .call(() => addPing(cx, cy, 0.5), undefined, 0.85)
            // Beat 4 — beam ignites, one fast decelerating revolution
            .to(beam, { alpha: 1, duration: 0.3 }, 0.9)
            .to(beam, { deg: 360 + LOCK_BEARING, duration: 1.5, ease: 'sweepBrake' }, 0.9)
            // Beat 5 — LOCK-ON at bearing 047
            .to(hairlines, { drawSVG: '100%', duration: 0.35, ease: 'power3.out' }, 2.05)
            .fromTo(
              brackets,
              { opacity: 0, x: (i) => (i % 2 === 0 ? -26 : 26), y: (i) => (i < 2 ? -26 : 26) },
              { opacity: 1, x: 0, y: 0, duration: 0.4, ease: 'power3.out' },
              2.15,
            )
            .call(() => addPing(cx, cy, 0.65, beamR * 0.5), undefined, 2.15)
            .to(cardWrap, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'expo.out' }, 2.15)
            .to(
              tagEl,
              {
                opacity: 1,
                duration: 0.9,
                scrambleText: { text: TAG_TEXT, chars: SCRAMBLE_CHARS, speed: 0.9 },
              },
              2.2,
            );
          if (logoStrokes.length) {
            master.fromTo(
              logoStrokes,
              { drawSVG: '0%' },
              { drawSVG: '100%', duration: 0.55, stagger: 0.12, ease: 'power2.inOut' },
              2.25,
            );
          }
          if (logoLens) {
            // the single brass glint the palette allows the logo
            master
              .fromTo(
                logoLens,
                { scale: 0, transformOrigin: 'center center', fill: BRASS },
                { scale: 1, duration: 0.35, ease: 'back.out(2.5)' },
                2.7,
              )
              .to(logoLens, { fill: `rgb(${PHOSPHOR_RGB})`, duration: 0.6 }, 2.95);
          }
          // Beat 6 — brackets settle, instrument at rest
          master
            .to(brackets, { opacity: 0.4, duration: 0.6 }, 2.9)
            .to(tagEl, { opacity: 0.75, duration: 0.6 }, 3.0)
            .to(hairlines, { opacity: 0.16, duration: 0.8 }, 2.9);

          // tagline rises per-character — gated on fonts so Polish
          // diacritics never re-split mid-animation
          if (tagline) {
            master.call(
              () => {
                document.fonts.ready.then(() => {
                  if (skipped) {
                    gsap.set(tagline, { opacity: 1 });
                    return;
                  }
                  const split = new SplitText(tagline, { type: 'chars' });
                  gsap.set(tagline, { opacity: 1 });
                  gsap.from(split.chars, {
                    y: 9,
                    opacity: 0,
                    duration: 0.4,
                    stagger: 0.018,
                    ease: 'power2.out',
                    onComplete: () => split.revert(),
                  });
                });
              },
              undefined,
              2.45,
            );
          }
        } else {
          // CONDENSED repeat-visit intro (~1.1 s) — daily users never wait
          gsap.set([ringMain, ringInner, ringOuter], { drawSVG: '100%', opacity: 0 });
          gsap.set(ticks, { opacity: 0 });
          gsap.set(hairlines, { drawSVG: '100%', opacity: 0 });
          beam.deg = LOCK_BEARING - 80;
          prevBeamDeg = beam.deg;
          if (tagline) gsap.set(tagline, { opacity: 1 });

          master
            .to(blackoutRef.current, { opacity: 0, duration: 0.3, ease: 'sine.out' }, 0)
            .to([ringMain, ringInner, ringOuter], { opacity: 1, duration: 0.4 }, 0.05)
            .to(ticks, { opacity: 1, duration: 0.35 }, 0.1)
            .to(readouts, { opacity: 1, duration: 0.4 }, 0.15)
            .to(beam, { alpha: 1, duration: 0.4 }, 0.1)
            .to(beam, { deg: LOCK_BEARING, duration: 0.9, ease: 'sweepBrake' }, 0.1)
            .to(cardWrap, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'expo.out' }, 0.35)
            .fromTo(
              brackets,
              { opacity: 0, x: (i) => (i % 2 === 0 ? -14 : 14), y: (i) => (i < 2 ? -14 : 14) },
              { opacity: 0.4, x: 0, y: 0, duration: 0.35, ease: 'power3.out' },
              0.55,
            )
            .to(hairlines, { opacity: 0.16, duration: 0.4 }, 0.55)
            .call(() => addPing(cx, cy, 0.4, beamR * 0.5), undefined, 0.55)
            .to(
              tagEl,
              {
                opacity: 0.75,
                duration: 0.5,
                scrambleText: { text: TAG_TEXT, chars: SCRAMBLE_CHARS, speed: 1.2 },
              },
              0.55,
            );
          if (logoStrokes.length) gsap.set(logoStrokes, { drawSVG: '100%' });
        }

        // interaction-skip: the user can always fast-forward the entrance
        const skip = () => {
          if (master.progress() < 1) {
            skipped = true;
            master.progress(1, false);
          }
        };
        root.addEventListener('pointerdown', skip);
        window.addEventListener('keydown', skip);
        skipCleanup = () => {
          root.removeEventListener('pointerdown', skip);
          window.removeEventListener('keydown', skip);
        };

        // ---- imperative API -------------------------------------------------
        apiRef.current = {
          submitPing: () => {
            addPing(cx, cy, 0.8, beamR * 0.9);
            // brackets tighten 4 px for a beat
            gsap.fromTo(
              brackets,
              { x: 0, y: 0 },
              {
                x: (i) => (i % 2 === 0 ? 4 : -4),
                y: (i) => (i < 2 ? 4 : -4),
                opacity: 1,
                duration: 0.18,
                yoyo: true,
                repeat: 1,
                ease: 'power2.inOut',
              },
            );
            // 10-bubble burst rising off the card's top edge (buoyancy)
            const rect = cardWrap.getBoundingClientRect();
            for (let i = 0; i < 10; i++) {
              const b = document.createElement('span');
              b.setAttribute('aria-hidden', 'true');
              const s = 3 + Math.random() * 5;
              Object.assign(b.style, {
                position: 'fixed',
                left: `${rect.left + 16 + Math.random() * (rect.width - 32)}px`,
                top: `${rect.top + 4}px`,
                width: `${s}px`,
                height: `${s}px`,
                borderRadius: '50%',
                border: `1px solid rgba(${PHOSPHOR_RGB},0.7)`,
                pointerEvents: 'none',
                zIndex: '30',
              } satisfies Partial<CSSStyleDeclaration>);
              root.appendChild(b);
              gsap.to(b, {
                duration: 1.4 + Math.random() * 0.5,
                physics2D: {
                  velocity: 55 + Math.random() * 60,
                  angle: -90 + (Math.random() * 40 - 20),
                  gravity: -70,
                },
                opacity: 0,
                ease: 'none',
                onComplete: () => b.remove(),
              });
            }
          },
          errorEcho: () => {
            // sonar operators don't panic: a calm double-echo, no red
            addPing(cx, cy, 0.45, beamR * 0.5);
            gsap.delayedCall(0.28, () => addPing(cx, cy, 0.3, beamR * 0.45));
            const tl = gsap.timeline();
            tl.to(tagEl, {
              duration: 0.45,
              opacity: 1,
              scrambleText: { text: TAG_RETRY, chars: SCRAMBLE_CHARS, speed: 1 },
            })
              .to(tagEl, {
                duration: 0.6,
                delay: 1.6,
                opacity: 0.75,
                scrambleText: { text: TAG_TEXT, chars: SCRAMBLE_CHARS, speed: 1 },
              });
          },
          surface: (onDone) => {
            // the boat surfaces into the app: depth grade lifts, one last rush
            addPing(cx, cy, 0.9, beamR);
            gsap.to(surfaceRef.current, { opacity: 1, duration: 0.55, ease: 'power2.in' });
            gsap.to(root, { opacity: 0, duration: 0.35, delay: 0.4, onComplete: onDone });
          },
        };
      }, root);

      return () => {
        for (const a of ambient) a.kill();
        gsap.killTweensOf(beam);
        gsap.ticker.remove(tick);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('keydown', onAnyInput);
        window.removeEventListener('pointerdown', onAnyInput);
        window.removeEventListener('resize', onResize);
        skipCleanup?.();
        gctx.revert();
        for (const el of svgKids) el.remove();
        apiRef.current = {
          submitPing: () => {},
          errorEcho: () => {},
          surface: (onDone) => onDone(),
        };
      };
    }, []);

    return (
      <div
        ref={rootRef}
        className="dark relative flex min-h-screen items-center justify-center overflow-hidden p-6"
        style={{
          background: `radial-gradient(120% 90% at 50% 12%, ${PRESSURE} 0%, ${INK} 62%, #060514 100%)`,
        }}
      >
        {/* fresh layer: marine snow, caustic shaft, ping rings */}
        <canvas ref={freshRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />
        {/* phosphor layer: sweep beam + contact blips with persistence */}
        <canvas ref={phosphorRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />
        {/* instrument linework: bearing ring, ticks, crosshair hairlines */}
        <svg ref={svgRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />
        {/* film grain: breaks up dark-gradient banding, adds CRT texture */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* instrument readouts */}
        <div
          ref={readoutLeftRef}
          aria-hidden="true"
          className="pointer-events-none absolute left-5 top-5 font-mono text-[10px] leading-relaxed tracking-[0.22em] text-indigo-300/70 select-none"
        >
          <div>{t('auth.scopeStation')}</div>
          <span ref={depthRef}>{t('auth.scopeDepth', { m: 14 })}</span>
        </div>
        <div
          ref={bearingRef}
          aria-hidden="true"
          className="pointer-events-none absolute right-5 top-5 font-mono text-[10px] tracking-[0.22em] text-indigo-300/70 select-none max-[420px]:hidden"
        >
          {t('auth.scopeBearing', { deg: '000.0' })}
        </div>

        {/* the identified contact */}
        <div ref={cardWrapRef} className="relative z-10 w-full max-w-sm">
          {/* brass classification tag */}
          <div
            ref={tagRef}
            aria-hidden="true"
            className="pointer-events-none absolute -top-8 left-0 whitespace-nowrap font-mono text-[11px] tracking-[0.18em] select-none max-[400px]:text-[9px] max-[400px]:tracking-[0.1em]"
            style={{ color: BRASS }}
          >
            {t('auth.scopeContact')}
          </div>
          {/* lock-on corner brackets */}
          {[
            'left-[-11px] top-[-11px] border-l border-t',
            'right-[-11px] top-[-11px] border-r border-t',
            'left-[-11px] bottom-[-11px] border-l border-b',
            'right-[-11px] bottom-[-11px] border-r border-b',
          ].map((pos) => (
            <span
              key={pos}
              data-scope-bracket
              aria-hidden="true"
              className={`pointer-events-none absolute size-5 border-indigo-300 ${pos}`}
            />
          ))}
          {children}
        </div>

        {/* surfacing lift on auth success */}
        <div
          ref={surfaceRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 opacity-0"
          style={{
            background: `linear-gradient(to bottom, rgba(${PHOSPHOR_RGB},0.35), rgba(${BEAM_RGB},0.12) 55%, transparent)`,
          }}
        />
        {/* boot blackout */}
        <div ref={blackoutRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-30 bg-black" />
      </div>
    );
  },
);
